import { ListStart, MoreHorizontal, Play, Plus, Search } from 'lucide-solid';
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  on,
  onCleanup,
  Show,
  untrack,
  type Accessor,
  type JSX,
  type Setter,
} from 'solid-js';
import { Portal } from 'solid-js/web';
import { createStore, produce, reconcile, unwrap } from 'solid-js/store';
import { CardSection, ConfirmModal, TechnicalDetails } from '../components/ui';
import {
  addAutoVJStep,
  addAutoVJUnit,
  audioPresetCanBuildStep,
  autoVJLookFromStack,
  autoVJLookToStack,
  autoVJSetParameters,
  autoVJSets,
  autoVJSetValues,
  autoVJStepChange,
  autoVJStepParameters,
  autoVJStepSummary,
  autoVJStepValues,
  composeAutoVJStack,
  copyAudioPresetIntoStep,
  emptyAutoVJStep,
  removeAutoVJStep,
  removeAutoVJUnit,
  reorderAutoVJUnit,
  type AutoVJLook,
  type AutoVJSet,
  type AutoVJStep,
  type AutoVJUnit,
} from '../lib/autoVJ';
import { AutoVJSetDraft, nextDefaultName } from '../lib/autoVJDraft';
import { AUTO_VJ_PACKAGE_ID, AUDIO_REACTIVE_PACKAGE_ID, type EffectPackageControlValue, type EffectPackageItem } from '../lib/effectPackages';
import type { EffectPreset } from '../lib/effectPresets';
import type { EffectStack, EffectStackPatch } from '../lib/effectStack';
import { can } from '../lib/permissions.generated';
import { plainError, type PlainError } from '../lib/plainErrors';
import { lookPlayable } from '../lib/playLook';
import { silenceFallbackControls } from '../lib/silenceFallback';
import type {
  SettingsTreeAction,
  SettingsTreeControl,
  SettingsTreeNode,
  SettingsTreePlus,
  SettingsTreeSource,
} from '../lib/settingsTree';
import { UNREPORTED_COMMAND_LIFETIME_MS, useVenue } from '../state/VenueProvider';
import {
  AudioStackAdd,
  audioStackPlus,
  createAudioStackNodes,
  layerName,
  stackLayerIds,
  type AudioStackModel,
} from './AudioStackEditor';
import { openSettingsTreeOn, SettingsTreeSheet } from './SettingsTreeSheet';
import './AutoVJEditor.css';
import { CLASS_NAMES, TEST_IDS, testIdFor } from '../testIds';

interface PendingConfirmation {
  title: string;
  confirmLabel: string;
  run: () => void;
}

function newId(kind: 'set' | 'unit' | 'step' | 'layer'): string {
  return `${kind}-${crypto.randomUUID()}`;
}

/**
 * Where one node sits in the room's sets. Tree ids are paths — a unit or step id is unique only inside
 * its set — so two sets copied from one another never share a node, an open state or a selection.
 */
type NodePlace =
  | { kind: 'set'; setId: string }
  | { kind: 'unit'; setId: string; unitId: string }
  | { kind: 'step'; setId: string; unitId: string; stepId: string }
  | { kind: 'layer'; setId: string; unitId: string; stepId: string; layerId: string };

const unitNodeId = (setId: string, unitId: string): string => `${setId}/${unitId}`;
const stepNodeId = (setId: string, unitId: string, stepId: string): string => `${setId}/${unitId}/${stepId}`;
const layerNodeId = (setId: string, unitId: string, stepId: string, layerId: string): string => `${setId}/${unitId}/${stepId}/${layerId}`;

/**
 * The Auto VJ card's authoring: the Sets list in its pane, and the tree sheet over the page (B84).
 *
 * The pane is the room's sets — the active-set radio, each name, its ⋯ menu and `+ Set`. Choosing a set,
 * or Edit in its menu, opens the tree sheet on it: sets › units › steps › layers on the left, the chosen
 * item's own settings on the right.
 *
 * Editing is local-first (S17 / B69). The set holding the selection is loaded into a fine-grained store
 * that every keystroke, click and drag writes synchronously, so the screen answers at once and no control
 * is ever disabled by a save. Each set has one `AutoVJSetDraft` that debounces the whole document out in
 * the background and derives Saved from the pushed room state. The room listener never writes into a
 * draft that carries edits: another client's change is offered as "Reload set".
 *
 * This is also the Auto VJ ADAPTER (S34): it hands the tree view nodes of the one shape every tree card
 * uses, and the view knows nothing about sets, units or steps.
 */
export function AutoVJEditor(props: {
  card: EffectPackageItem;
  packages: readonly EffectPackageItem[];
  /** Play can go right now: a controller, the room layout and a music input. Role gating is separate. */
  canPlay: Accessor<boolean>;
  /** Put a look on the room and hold it until Stop (B72). */
  onPlay: (look: AutoVJLook) => Promise<void>;
  /** Auto VJ can start right now: a controller, the room layout, the card available. */
  canStartFrom: Accessor<boolean>;
  /** Run a set from one of its units onward (B73). */
  onStartFrom: (setId: string, unitId: string) => Promise<void>;
}): JSX.Element {
  const venue = useVenue();
  const [loadedId, setLoadedId] = createSignal('');
  const [draft, setDraft] = createStore<{ set?: AutoVJSet }>({});
  const [version, setVersion] = createSignal(0);
  const [query, setQuery] = createSignal('');
  const [menuId, setMenuId] = createSignal('');
  const [renamingId, setRenamingId] = createSignal('');
  const [renameValue, setRenameValue] = createSignal('');
  const [deletedIds, setDeletedIds] = createSignal<ReadonlySet<string>>(new Set());
  const [activeOverride, setActiveOverride] = createSignal<string>();
  const [listProblem, setListProblem] = createSignal<PlainError>();
  const [confirmation, setConfirmation] = createSignal<PendingConfirmation>();
  const [sheetOpen, setSheetOpen] = createSignal(false);
  const view = createMemo(() => venue.settingsTreeView.view(
    `${venue.selectedVenue()?.venueId ?? ''}:${venue.selectedRoom()?.roomId ?? ''}:${AUTO_VJ_PACKAGE_ID}`,
  ));
  const drafts = new Map<string, AutoVJSetDraft>();
  const stacks = new Map<string, [Accessor<EffectStack>, Setter<EffectStack>]>();
  const timers = new Set<ReturnType<typeof setTimeout>>();

  const pushedSets = createMemo(() => new Map(autoVJSets(venue.autoVJLibrary()).map((set) => [set.id, set])));
  const activeId = createMemo(() => activeOverride() ?? venue.autoVJLibrary().activeSetId);
  const canManage = createMemo(() => can(venue.role(), 'effects.packages.manage'));
  // A role without effects.run never sees Play (UI-STYLE Q14); `canPlay` covers only passing states.
  const canRun = createMemo(() => can(venue.role(), 'effects.run'));
  const disabled = createMemo(() => !canManage() || !venue.controlsEnabled());
  const disabledReason = createMemo(() => !canManage()
    ? 'You do not have permission to do that.'
    : !venue.selectedDeviceId() ? 'Choose a controller.'
      : !venue.controlsEnabled() ? 'The controller is offline.' : '');
  const presets = createMemo(() => venue.effectPresets().filter((preset) => (
    preset.packageId === AUDIO_REACTIVE_PACKAGE_ID && audioPresetCanBuildStep(preset)
  )));
  const zoneNames = createMemo(() => new Map((venue.roomModel()?.zones ?? []).map((zone) => [zone.id, zone.name])));

  function later(run: () => void, ms: number): void {
    const handle = globalThis.setTimeout(() => {
      timers.delete(handle);
      run();
    }, ms);
    timers.add(handle);
  }

  function draftFor(id: string, initial?: AutoVJSet): AutoVJSetDraft | undefined {
    const existing = drafts.get(id);
    if (existing) return existing;
    const pushed = pushedSets().get(id);
    const source = initial ?? pushed;
    if (!source) return undefined;
    const created = new AutoVJSetDraft(source, {
      send: (set) => venue.controllerCall('set_effect_document', {
        effectId: AUTO_VJ_PACKAGE_ID,
        kind: 'set',
        id: set.id,
        data: set,
      }),
      onChange: () => setVersion((current) => current + 1),
      unreportedMs: UNREPORTED_COMMAND_LIFETIME_MS,
    }, { published: !initial && Boolean(pushed) });
    drafts.set(id, created);
    return created;
  }

  // ---- The loaded set's store ---------------------------------------------------------------------

  /** Hand the store's current value to the loaded set's draft, which schedules the background save. */
  function commit(): void {
    const set = draft.set;
    if (set) drafts.get(set.id)?.edit(unwrap(set));
  }

  function load(id: string): void {
    if (loadedId() === id && draft.set) return;
    const machine = draftFor(id);
    if (!machine) return;
    const previous = drafts.get(loadedId());
    if (previous && previous !== machine) void previous.flush();
    stacks.clear();
    setDraft('set', machine.value());
    setLoadedId(id);
  }

  function unload(): void {
    stacks.clear();
    setDraft('set', undefined);
    setLoadedId('');
  }

  /** Replace the loaded draft with the room's set, keeping every unchanged node (keyed by id). */
  function adopt(set: AutoVJSet): void {
    setDraft('set', reconcile(structuredClone(set), { key: 'id', merge: false }));
    for (const unit of set.units ?? []) {
      for (const step of unit.steps ?? []) stacks.get(step.id)?.[1](autoVJLookToStack(step.look, step.id));
    }
  }

  function reloadSet(id: string): void {
    const machine = drafts.get(id);
    const pushed = pushedSets().get(id);
    if (!machine) return;
    if (!pushed) {
      machine.dispose();
      drafts.delete(id);
      if (loadedId() === id) unload();
      return;
    }
    machine.reset(pushed);
    if (loadedId() === id) adopt(pushed);
  }

  // The room's listener. It confirms saves and reports other clients' changes to each draft; the only
  // write it ever makes into the loaded store is adopting a change while the draft is still pristine.
  createEffect(on(() => venue.effectDocuments(), () => {
    const pushed = pushedSets();
    for (const [id, machine] of [...drafts]) {
      if (deletedIds().has(id)) continue;
      if (machine.observe(pushed.get(id)) !== 'adopt') continue;
      const next = pushed.get(id);
      if (!next) {
        machine.dispose();
        drafts.delete(id);
        if (loadedId() === id) unload();
        continue;
      }
      machine.reset(next);
      if (loadedId() === id) adopt(next);
    }
    // Optimistic list state ends when the room shows it — or never needed to.
    setDeletedIds((current) => {
      const confirmed = [...current].filter((id) => !pushed.has(id));
      return confirmed.length ? new Set([...current].filter((id) => pushed.has(id))) : current;
    });
    if (activeOverride() !== undefined && venue.autoVJLibrary().activeSetId === activeOverride()) {
      setActiveOverride(undefined);
    }
  }));

  // Closing the card drains every draft: flush sends a pending edit now, and a save already on the wire
  // still sends the edit queued behind it when it lands. Disposing here would drop that queued edit.
  onCleanup(() => {
    for (const machine of drafts.values()) void machine.flush();
    for (const handle of timers) globalThis.clearTimeout(handle);
  });

  // ---- Reading any set: the loaded one from its store, the rest from their drafts or the room -------

  function setData(id: string): AutoVJSet | undefined {
    if (loadedId() === id && draft.set) return draft.set;
    version();
    return drafts.get(id)?.value() ?? pushedSets().get(id);
  }

  function unitOf(setId: string, unitId: string): AutoVJUnit | undefined {
    return setData(setId)?.units?.find((unit) => unit.id === unitId);
  }

  function stepOf(setId: string, unitId: string, stepId: string): AutoVJStep | undefined {
    return unitOf(setId, unitId)?.steps?.find((step) => step.id === stepId);
  }

  /**
   * A step's look as the shared stack model edits it: one signal per step of the LOADED set, created
   * from the stored look on first read and moved only by that step's own edits, so an edit elsewhere
   * never hands the stack new layer arrays. A step of another set reads its stored look.
   */
  function stackOf(setId: string, unitId: string, stepId: string): EffectStack {
    const step = stepOf(setId, unitId, stepId);
    if (!step) return { mix: 'max', layers: [] };
    if (loadedId() !== setId) return autoVJLookToStack(step.look, step.id);
    const existing = stacks.get(step.id);
    if (existing) return existing[0]();
    // The store's step is a proxy; the stack is built from the plain value underneath it.
    const created = createSignal<EffectStack>(autoVJLookToStack(unwrap(step).look, step.id));
    stacks.set(step.id, created);
    return created[0]();
  }

  // ---- Set-level edits ------------------------------------------------------------------------------

  function setField<K extends 'name' | 'ordering' | 'noRepeatWithin' | 'musicEventSensitivity' | 'silenceSpeed' | 'silenceLevel'>(key: K, value: AutoVJSet[K]): void {
    if (!draft.set || disabled()) return;
    setDraft('set', key as never, value as never);
    commit();
  }

  /**
   * Apply a structural edit from `lib/autoVJ` (which owns its rules) to the loaded set. The result is
   * reconciled by id, so every unit and step that did not change keeps its store node and its DOM.
   */
  function restructure(edit: (set: AutoVJSet) => AutoVJSet): void {
    const set = draft.set;
    if (!set || disabled()) return;
    setDraft('set', reconcile(edit(unwrap(set)), { key: 'id', merge: false }));
    commit();
  }

  /**
   * `+ Unit` and `+ Step` open the item they add to, so what they add is in the tree at once, and return
   * the new item's TREE id for the view to select (B89) — or nothing, when there was nothing to add to.
   */
  function addUnit(setId: string): string | undefined {
    load(setId);
    if (loadedId() !== setId || !draft.set || disabled()) return undefined;
    view().setOpen(setId, true);
    const id = newId('unit');
    restructure((set) => addAutoVJUnit(set, {
      id,
      name: nextDefaultName('Unit', (set.units ?? []).map((item) => item.name)),
      steps: [],
    }));
    return unitNodeId(setId, id);
  }

  function renameUnit(unitId: string, name: string): void {
    if (disabled()) return;
    setDraft('set', 'units', (unit) => unit.id === unitId, 'name', name);
    commit();
  }

  function addStep(setId: string, unitId: string): string | undefined {
    load(setId);
    const unit = unitOf(setId, unitId);
    if (!unit || loadedId() !== setId || !draft.set || disabled()) return undefined;
    view().setOpen(unitNodeId(setId, unitId), true);
    const id = newId('step');
    const step = emptyAutoVJStep(id, nextDefaultName('Step', (unit.steps ?? []).map((item) => item.name)));
    restructure((set) => addAutoVJStep(set, unitId, step));
    return stepNodeId(setId, unitId, id);
  }

  function patchStep(unitId: string, stepId: string, patch: Partial<Pick<AutoVJStep, 'name' | 'look' | 'dwellMinBars' | 'dwellMaxBars' | 'transition'>>): void {
    if (disabled()) return;
    setDraft('set', 'units', (item) => item.id === unitId, 'steps', (step) => step.id === stepId, produce((step) => {
      Object.assign(step, structuredClone(patch));
    }));
    commit();
  }

  function setStack(setId: string, unitId: string, stepId: string, next: EffectStack): void {
    stackOf(setId, unitId, stepId);
    stacks.get(stepId)?.[1](next);
    patchStep(unitId, stepId, { look: autoVJLookFromStack(next) });
  }

  function editLook(setId: string, unitId: string, stepId: string, patch: EffectStackPatch): void {
    if (disabled()) return;
    load(setId);
    try {
      setStack(setId, unitId, stepId, composeAutoVJStack(stackOf(setId, unitId, stepId), patch, props.packages, newId('layer')));
    } catch (error) {
      setListProblem(plainError(error));
    }
  }

  function startFromPreset(setId: string, unitId: string, stepId: string, chosen: EffectPreset): void {
    if (disabled()) return;
    load(setId);
    const look = copyAudioPresetIntoStep(chosen, stepId).look;
    setStack(setId, unitId, stepId, autoVJLookToStack(look, stepId));
  }

  /**
   * Start from here runs what the ROOM holds, so the unit must already be in the saved set and have a
   * step that paints; an unsaved or empty unit waits (the save lands within a beat of the edit).
   */
  function unitStartable(setId: string, unitId: string): boolean {
    version();
    const saved = pushedSets().get(setId)?.units?.find((candidate) => candidate.id === unitId);
    return Boolean(saved && (saved.steps ?? []).some((step) => lookPlayable(step.look)));
  }

  /** A step's look as the editor shows it right now — the draft, saved or not, is what Play auditions. */
  function lookOf(setId: string, unitId: string, stepId: string): AutoVJLook {
    return autoVJLookFromStack(stackOf(setId, unitId, stepId));
  }

  // ---- The sets list ----------------------------------------------------------------------------

  function nameOf(id: string): string {
    version();
    if (loadedId() === id && draft.set) return draft.set.name;
    return drafts.get(id)?.name() ?? pushedSets().get(id)?.name ?? '';
  }

  /** The room's sets plus any this console created that the room has not published yet, by name. */
  const setIds = createMemo(() => {
    version();
    const ids = new Set([...pushedSets().keys(), ...drafts.keys()]);
    for (const id of deletedIds()) ids.delete(id);
    return [...ids].sort((left, right) => nameOf(left).localeCompare(nameOf(right)) || left.localeCompare(right));
  }, [], { equals: (left, right) => left.length === right.length && left.every((id, index) => id === right[index]) });
  const visibleSetIds = createMemo(() => {
    const needle = query().trim().toLocaleLowerCase();
    return needle ? setIds().filter((id) => nameOf(id).toLocaleLowerCase().includes(needle)) : setIds();
  });

  /** Load a set: the one set whose edits the store holds. */
  function selectSet(id: string): void {
    setMenuId('');
    load(id);
  }

  /** Choosing a set's name, or Edit in its menu, opens the sheet on it (B84). */
  function openSet(id: string): void {
    selectSet(id);
    openSettingsTreeOn(view(), id);
    setSheetOpen(true);
  }

  /** A committed rename of a set. A blank commit keeps the old name. */
  function commitSetName(id: string, value: string): void {
    const name = value.trim();
    if (!name || name === nameOf(id) || disabled()) return;
    load(id);
    setField('name', name);
  }

  async function setActive(id: string): Promise<boolean> {
    setActiveOverride(id);
    setListProblem(undefined);
    try {
      await venue.controllerCall('set_effect_document', {
        effectId: AUTO_VJ_PACKAGE_ID,
        kind: 'active',
        id: 'active',
        data: { activeSetId: id },
      });
      later(() => { if (activeOverride() === id) setActiveOverride(undefined); }, UNREPORTED_COMMAND_LIFETIME_MS);
      return true;
    } catch (error) {
      if (activeOverride() === id) setActiveOverride(undefined);
      setListProblem(plainError(error));
      return false;
    }
  }

  async function createSet(): Promise<void> {
    if (disabled()) return;
    const set: AutoVJSet = {
      id: newId('set'),
      name: nextDefaultName('Set', setIds().map(nameOf)),
      ordering: 'deterministic',
      noRepeatWithin: 0,
      musicEventSensitivity: 0.5,
      ...Object.fromEntries(silenceFallbackControls(props.card).map(({ key, spec }) => [key, spec.default])),
      units: [],
    };
    const machine = draftFor(set.id, set);
    if (!machine) return;
    machine.edit(set);
    selectSet(set.id);
    const hadActive = Boolean(activeId());
    await machine.flush();
    if (!hadActive && machine.status().kind !== 'refused') await setActive(set.id);
  }

  function beginRename(id: string): void {
    setMenuId('');
    setRenamingId(id);
    setRenameValue(nameOf(id));
  }

  function renameSet(id: string): void {
    const name = renameValue().trim();
    setRenamingId('');
    if (!name || name === nameOf(id) || disabled()) return;
    if (loadedId() === id) {
      setField('name', name);
      void drafts.get(id)?.flush();
      return;
    }
    const machine = draftFor(id);
    if (!machine) return;
    machine.edit({ ...machine.value(), name });
    void machine.flush();
  }

  async function deleteSet(id: string): Promise<void> {
    setMenuId('');
    setListProblem(undefined);
    const machine = drafts.get(id);
    machine?.dispose();
    drafts.delete(id);
    if (loadedId() === id) unload();
    setDeletedIds((current) => new Set([...current, id]));
    const restore = (error: unknown) => {
      setDeletedIds((current) => new Set([...current].filter((item) => item !== id)));
      setListProblem(plainError(error));
    };
    // The active pointer is cleared FIRST; a set whose pointer could not be cleared is not deleted.
    if (activeId() === id && !await setActive('')) {
      setDeletedIds((current) => new Set([...current].filter((item) => item !== id)));
      return;
    }
    try {
      await venue.controllerCall('delete_effect_document', { effectId: AUTO_VJ_PACKAGE_ID, kind: 'set', id });
      later(() => setDeletedIds((current) => new Set([...current].filter((item) => item !== id))), UNREPORTED_COMMAND_LIFETIME_MS);
    } catch (error) {
      restore(error);
    }
  }

  function ask(title: string, confirmLabel: string, run: () => void): void {
    setMenuId('');
    setConfirmation({ title, confirmLabel, run });
  }

  // ---- Save state of the set holding the selection --------------------------------------------------

  const runningSetId = createMemo(() => {
    const direct = venue.controllerState()?.effects?.find((effect) => effect.kind === 'auto_vj')?.autoVJ;
    const mirrored = venue.mirror()?.effect?.kind === 'auto_vj'
      ? venue.mirror()?.effect?.autoVJ as { setId?: string } | null | undefined
      : undefined;
    return String(direct?.setId ?? mirrored?.setId ?? '');
  });

  function saveStatus(setId: string) {
    version();
    return drafts.get(setId)?.status() ?? { kind: 'idle' as const };
  }

  function saveText(setId: string): string {
    const status = saveStatus(setId);
    switch (status.kind) {
      case 'pending':
      case 'saving': return 'Saving…';
      case 'saved': return runningSetId() === setId ? 'Saved — applies on next Start' : 'Saved';
      case 'invalid':
      case 'refused': return status.problem.message;
      default: return '';
    }
  }

  function saveDetail(setId: string): string {
    const status = saveStatus(setId);
    return status.kind === 'invalid' || status.kind === 'refused' ? status.problem.detail : '';
  }

  function remoteChanged(setId: string): boolean {
    version();
    return drafts.get(setId)?.remoteChanged() ?? false;
  }

  const feedback = createMemo(() => disabledReason() || listProblem()?.message || '');

  // ---- The tree: the Auto VJ adapter (S34) -------------------------------------------------------------

  /** Every node's place, rebuilt only when the SHAPE of the sets changes (ids, not names or values). */
  const places = createMemo(() => {
    const index = new Map<string, NodePlace>();
    for (const setId of setIds()) {
      index.set(setId, { kind: 'set', setId });
      for (const unit of setData(setId)?.units ?? []) {
        index.set(unitNodeId(setId, unit.id), { kind: 'unit', setId, unitId: unit.id });
        for (const step of unit.steps ?? []) {
          index.set(stepNodeId(setId, unit.id, step.id), { kind: 'step', setId, unitId: unit.id, stepId: step.id });
          for (const layerId of stackLayerIds(stackOf(setId, unit.id, step.id).layers)) {
            index.set(layerNodeId(setId, unit.id, step.id, layerId), { kind: 'layer', setId, unitId: unit.id, stepId: step.id, layerId });
          }
        }
      }
    }
    return index;
  });

  const nodes = new Map<string, SettingsTreeNode>();
  const stepLayers = new Map<string, (layerId: string) => SettingsTreeNode | undefined>();

  function itemName(effectId: string): string {
    return layerName(props.packages.find((item) => item.packageId === effectId), effectId);
  }

  /** One step's composed look as the shared audio stack model (the same adapter Audio reactive uses). */
  function stepStackModel(setId: string, unitId: string, stepId: string): AudioStackModel {
    return {
      effectId: () => stepId,
      items: () => [...props.packages],
      layers: () => stackOf(setId, unitId, stepId).layers,
      reported: () => stackOf(setId, unitId, stepId).layers,
      zoneNames,
      inFlight: () => false,
      disabled,
      compose: true,
      onPatch: (patch) => editLook(setId, unitId, stepId, patch),
      afterRemove: (control) => control.select(stepNodeId(setId, unitId, stepId)),
      nodeId: (layerId) => layerNodeId(setId, unitId, stepId, layerId),
    };
  }

  function layerNodes(setId: string, unitId: string, stepId: string): (layerId: string) => SettingsTreeNode | undefined {
    const key = stepNodeId(setId, unitId, stepId);
    let reader = stepLayers.get(key);
    if (!reader) {
      reader = createAudioStackNodes(stepStackModel(setId, unitId, stepId));
      stepLayers.set(key, reader);
    }
    return reader;
  }

  const stepParameters = new Map<number, EffectPackageItem>();
  function stepSpec(dwellMinBars: number): EffectPackageItem {
    let spec = stepParameters.get(dwellMinBars);
    if (!spec) stepParameters.set(dwellMinBars, spec = autoVJStepParameters(dwellMinBars));
    return spec;
  }

  function setNode(setId: string): SettingsTreeNode {
    const rename: SettingsTreeAction = { label: 'Rename', get disabled() { return disabled(); }, run: (control) => control.focusHead() };
    const remove: SettingsTreeAction = {
      label: 'Delete',
      danger: true,
      get disabled() { return disabled(); },
      run: (control) => ask('Delete set?', 'Delete', () => {
        control.select('');
        void deleteSet(setId);
      }),
    };
    const actions = [rename, remove];
    const plus: SettingsTreePlus = { label: 'Add unit', get disabled() { return disabled(); }, run: () => addUnit(setId) };
    return {
      id: setId,
      get name() { return nameOf(setId); },
      get children() { return (setData(setId)?.units ?? []).map((unit) => unitNodeId(setId, unit.id)); },
      get parameters() { return autoVJSetParameters((setData(setId)?.units ?? []).length, props.card); },
      get values() { const set = setData(setId); return set ? autoVJSetValues(set, props.card) : {}; },
      parameterPrefix: testIdFor.autoVJSet(setId),
      onParameters: (values, key) => {
        load(setId);
        const value = values[key];
        if (key === 'ordering' && (value === 'deterministic' || value === 'random')) setField('ordering', value);
        if (key === 'noRepeatWithin' && typeof value === 'number' && Number.isFinite(value)) setField('noRepeatWithin', value);
        if (key === 'musicEventSensitivity' && typeof value === 'number' && Number.isFinite(value)) setField('musicEventSensitivity', value);
        if ((key === 'silenceSpeed' || key === 'silenceLevel') && typeof value === 'number'
            && Number.isFinite(value) && silenceFallbackControls(props.card).some((control) => control.key === key)) {
          setField(key, value);
        }
      },
      get actions() { return canManage() ? actions : []; },
      // The set name commits when the field is left or on Enter (B66), because the sets list sorts by it.
      head: () => (
        <input
          value={nameOf(setId)}
          maxlength={64}
          aria-label="Set name"
          disabled={disabled()}
          onChange={(event) => {
            commitSetName(setId, event.currentTarget.value);
            event.currentTarget.value = nameOf(setId);
          }}
          onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}
        />
      ),
      get plus() { return canManage() ? plus : undefined; },
      get add() {
        return canManage() ? (control: SettingsTreeControl) => (
          <button
            type="button"
            class="button button-secondary"
            aria-label={plus.label}
            disabled={disabled()}
            onClick={() => control.added(plus.run())}
            data-testid={TEST_IDS.autoVjAddUnit}
          ><Plus size={16} /> Unit</button>
        ) : undefined;
      },
      get disabled() { return disabled(); },
    };
  }

  function unitNode(setId: string, unitId: string): SettingsTreeNode {
    const units = (): AutoVJUnit[] => setData(setId)?.units ?? [];
    const index = (): number => units().findIndex((unit) => unit.id === unitId);
    const unit = (): AutoVJUnit | undefined => unitOf(setId, unitId);
    const firstStep = (): AutoVJStep | undefined => unit()?.steps?.[0];
    const play: SettingsTreeAction = {
      icon: Play,
      get label() { return `Play unit: ${unit()?.name ?? ''}`; },
      get disabled() { const step = firstStep(); return !props.canPlay() || !step || !lookPlayable(lookOf(setId, unitId, step.id)); },
      run: () => { const step = firstStep(); if (step) void props.onPlay(lookOf(setId, unitId, step.id)); },
    };
    const startFrom: SettingsTreeAction = {
      icon: ListStart,
      get label() { return `Start from here: ${unit()?.name ?? ''}`; },
      get disabled() { return !props.canStartFrom() || !unitStartable(setId, unitId); },
      run: () => void props.onStartFrom(setId, unitId),
    };
    const rename: SettingsTreeAction = { label: 'Rename', get disabled() { return disabled(); }, run: (control) => control.focusHead() };
    const moveUp: SettingsTreeAction = {
      label: 'Move up', get disabled() { return disabled(); },
      run: () => { load(setId); restructure((set) => reorderAutoVJUnit(set, unitId, -1)); },
    };
    const moveDown: SettingsTreeAction = {
      label: 'Move down', get disabled() { return disabled(); },
      run: () => { load(setId); restructure((set) => reorderAutoVJUnit(set, unitId, 1)); },
    };
    const remove: SettingsTreeAction = {
      label: 'Remove',
      danger: true,
      get disabled() { return disabled(); },
      run: (control) => ask('Remove unit?', 'Remove', () => {
        load(setId);
        restructure((set) => removeAutoVJUnit(set, unitId));
        control.select(setId);
      }),
    };
    const plus: SettingsTreePlus = {
      get label() { return `Add step: ${unit()?.name ?? ''}`; },
      get disabled() { return disabled(); },
      run: () => addStep(setId, unitId),
    };
    let shown: SettingsTreeAction[] = [];
    return {
      id: unitNodeId(setId, unitId),
      get name() { return unit()?.name ?? ''; },
      get children() { return (unit()?.steps ?? []).map((step) => stepNodeId(setId, unitId, step.id)); },
      get actions() {
        // A unit plays its first step's look: the one to audition (B72). Edge moves are omitted.
        const next = [
          ...(canRun() && (unit()?.steps ?? []).length > 0 ? [play, startFrom] : []),
          ...(canManage() ? [rename, ...(index() > 0 ? [moveUp] : []), ...(index() < units().length - 1 ? [moveDown] : []), remove] : []),
        ];
        if (next.length !== shown.length || next.some((entry, position) => entry !== shown[position])) shown = next;
        return shown;
      },
      head: () => (
        <input
          value={unit()?.name ?? ''}
          maxlength={64}
          disabled={disabled()}
          aria-label={`Unit ${index() + 1} name`}
          onInput={(event) => { load(setId); renameUnit(unitId, event.currentTarget.value); }}
        />
      ),
      get plus() { return canManage() ? plus : undefined; },
      get add() {
        return canManage() ? (control: SettingsTreeControl) => (
          <button
            type="button"
            class="button button-secondary"
            aria-label={plus.label}
            disabled={disabled()}
            onClick={() => control.added(plus.run())}
            data-testid={TEST_IDS.autoVjAddStep}
          ><Plus size={16} /> Step</button>
        ) : undefined;
      },
      get disabled() { return disabled(); },
    };
  }

  function stepNode(setId: string, unitId: string, stepId: string): SettingsTreeNode {
    const unitIndex = (): number => (setData(setId)?.units ?? []).findIndex((unit) => unit.id === unitId);
    const step = (): AutoVJStep | undefined => stepOf(setId, unitId, stepId);
    const stepIndex = (): number => (unitOf(setId, unitId)?.steps ?? []).findIndex((entry) => entry.id === stepId);
    const play: SettingsTreeAction = {
      icon: Play,
      get label() { return `Play step: ${step()?.name ?? ''}`; },
      get disabled() { return !props.canPlay() || !lookPlayable(lookOf(setId, unitId, stepId)); },
      run: () => void props.onPlay(lookOf(setId, unitId, stepId)),
    };
    const rename: SettingsTreeAction = { label: 'Rename', get disabled() { return disabled(); }, run: (control) => control.focusHead() };
    const remove: SettingsTreeAction = {
      label: 'Remove',
      danger: true,
      get disabled() { return disabled(); },
      run: (control) => ask('Remove step?', 'Remove', () => {
        load(setId);
        stacks.delete(stepId);
        restructure((set) => removeAutoVJStep(set, unitId, stepId));
        control.select(unitNodeId(setId, unitId));
      }),
    };
    const model = stepStackModel(setId, unitId, stepId);
    const plus = audioStackPlus(model);
    let shown: SettingsTreeAction[] = [];
    return {
      id: stepNodeId(setId, unitId, stepId),
      get name() { return step()?.name ?? ''; },
      get summary() { const current = step(); return current ? autoVJStepSummary(current, itemName) : undefined; },
      get children() { return stackLayerIds(stackOf(setId, unitId, stepId).layers).map((layerId) => layerNodeId(setId, unitId, stepId, layerId)); },
      get parameters() { return stepSpec(step()?.dwellMinBars ?? 1); },
      get values() {
        const current = step();
        if (!current) return {};
        // Mix is the look's, and the look the editor shows is the step's stack.
        return { ...autoVJStepValues(current), mix: stackOf(setId, unitId, stepId).mix } as Record<string, EffectPackageControlValue>;
      },
      parameterPrefix: testIdFor.autoVJStep(stepId),
      onParameters: (values) => {
        const current = step();
        if (!current) return;
        load(setId);
        const change = autoVJStepChange(current, values);
        if (change.mix) editLook(setId, unitId, stepId, { mix: change.mix });
        if (Object.keys(change.step).length) patchStep(unitId, stepId, change.step);
      },
      get actions() {
        const next = [...(canRun() ? [play] : []), ...(canManage() ? [rename, remove] : [])];
        if (next.length !== shown.length || next.some((entry, position) => entry !== shown[position])) shown = next;
        return shown;
      },
      head: () => (
        <input
          value={step()?.name ?? ''}
          maxlength={64}
          disabled={disabled()}
          aria-label={`Unit ${unitIndex() + 1} step ${stepIndex() + 1} name`}
          onInput={(event) => { load(setId); patchStep(unitId, stepId, { name: event.currentTarget.value }); }}
        />
      ),
      get controls() {
        return presets().length > 0 && canManage() ? () => (
          <select
            aria-label={`Start from preset: ${step()?.name ?? ''}`}
            value=""
            disabled={disabled()}
            onChange={(event) => {
              const chosen = presets().find((item) => item.presetId === event.currentTarget.value);
              if (chosen) startFromPreset(setId, unitId, stepId, chosen);
              event.currentTarget.value = '';
            }}
          >
            <option value="">Start from preset</option>
            <For each={presets()}>{(item) => <option value={item.presetId}>{item.name}</option>}</For>
          </select>
        ) : undefined;
      },
      get plus() { return canManage() ? plus : undefined; },
      get add() {
        return canManage() ? (control: SettingsTreeControl) => <AudioStackAdd model={model} onAdded={control.added} /> : undefined;
      },
      get disabled() { return disabled(); },
    };
  }

  function node(id: string): SettingsTreeNode | undefined {
    const place = places().get(id);
    if (!place) return undefined;
    if (place.kind === 'layer') return layerNodes(place.setId, place.unitId, place.stepId)(place.layerId);
    let found = nodes.get(id);
    if (!found) {
      found = place.kind === 'set' ? setNode(place.setId)
        : place.kind === 'unit' ? unitNode(place.setId, place.unitId)
          : stepNode(place.setId, place.unitId, place.stepId);
      nodes.set(id, found);
    }
    return found;
  }

  /** The set holding a node: the one the store must hold before any of the node's edits land. */
  function setOfNode(id: string): string {
    return places().get(id)?.setId ?? '';
  }

  const source: SettingsTreeSource = {
    roots: setIds,
    node,
    onSelect: (id) => {
      const setId = setOfNode(id);
      if (setId) load(setId);
    },
    saveSlot: (selectedId) => {
      const setId = setOfNode(selectedId);
      return (
        <Show when={setId}>
          <Show
            when={remoteChanged(setId)}
            fallback={(
              <span
                class="auto-vj-save-state"
                classList={{ 'form-error': saveStatus(setId).kind === 'refused' || saveStatus(setId).kind === 'invalid' }}
                data-testid={TEST_IDS.autoVjSaveState}
              >{saveText(setId)}</span>
            )}
          >
            <span class="auto-vj-save-state" data-testid={TEST_IDS.autoVjSaveState}>
              <button type="button" class="text-button" onClick={() => reloadSet(setId)}>Reload set</button>
            </span>
          </Show>
        </Show>
      );
    },
    saveDetail: (selectedId) => {
      const setId = setOfNode(selectedId);
      return (
        <Show when={setId && saveDetail(setId) && !remoteChanged(setId)}>
          <TechnicalDetails detail={saveDetail(setId)} testId={TEST_IDS.autoVjSaveDetails} />
        </Show>
      );
    },
  };

  // The sheet reopened on an earlier selection edits the set that holds it.
  createEffect(on(sheetOpen, (open) => {
    if (!open) return;
    const setId = setOfNode(untrack(() => view().selected()));
    if (setId) untrack(() => load(setId));
  }));

  return (
    <>
      <div class="auto-vj-editor" data-testid={TEST_IDS.autoVjEditor}>
        <Show when={feedback()}>
          <p class="auto-vj-feedback" classList={{ 'form-error': !disabledReason() }} role="status" data-testid={TEST_IDS.autoVjFeedback}>{feedback()}</p>
          <Show when={!disabledReason()}><TechnicalDetails detail={listProblem()?.detail} testId={TEST_IDS.autoVjFeedbackDetails} /></Show>
        </Show>

        <CardSection
          class="auto-vj-sets"
          label="Sets"
          tools={(
            <label class="library-search">
              <Search size={17} aria-hidden="true" />
              <input
                type="search"
                value={query()}
                onInput={(event) => setQuery(event.currentTarget.value)}
                placeholder="Search sets"
                aria-label="Search sets"
              />
            </label>
          )}
          footer={(
            <Show when={canManage()}>
              <div class="auto-vj-list-footer">
                <button
                  type="button"
                  class="button button-secondary"
                  disabled={disabled()}
                  onClick={() => void createSet()}
                  aria-label="Add set"
                  data-testid={TEST_IDS.autoVjAddSet}
                ><Plus size={16} /> Set</button>
              </div>
            </Show>
          )}
        >
          <For each={visibleSetIds()}>
            {(id) => (
              <div class={CLASS_NAMES.autoVJSetRow} classList={{ [CLASS_NAMES.selected]: loadedId() === id, readonly: !canManage() }}>
                <input
                  type="radio"
                  name="active-auto-vj-set"
                  aria-label={`Active set: ${nameOf(id)}`}
                  checked={activeId() === id}
                  disabled={disabled()}
                  onChange={() => void setActive(id)}
                />
                <Show
                  when={renamingId() === id}
                  fallback={(
                    <button
                      type="button"
                      class={CLASS_NAMES.autoVJSetOpen}
                      data-testid={TEST_IDS.autoVjSetOpen}
                      data-active={activeId() === id ? 'true' : 'false'}
                      onClick={() => openSet(id)}
                    >{nameOf(id)}</button>
                  )}
                >
                  <form class="scene-rename" onSubmit={(event) => { event.preventDefault(); renameSet(id); }}>
                    <input
                      value={renameValue()}
                      maxlength={64}
                      onInput={(event) => setRenameValue(event.currentTarget.value)}
                      aria-label={`Rename ${nameOf(id)}`}
                      autofocus
                    />
                    <button type="submit" class="text-button" disabled={!renameValue().trim()}>Save</button>
                    <button type="button" class="text-button" onClick={() => setRenamingId('')}>Cancel</button>
                  </form>
                </Show>
                <Show when={canManage() && renamingId() !== id}>
                  <div class="scene-menu">
                    <button
                      type="button"
                      class="icon-button"
                      aria-label={`More actions for ${nameOf(id)}`}
                      aria-expanded={menuId() === id}
                      disabled={disabled()}
                      onClick={() => setMenuId((current) => current === id ? '' : id)}
                    ><MoreHorizontal size={18} /></button>
                    <Show when={menuId() === id}>
                      <div class="scene-menu-popover" role="menu">
                        <button type="button" role="menuitem" onClick={() => beginRename(id)}>Rename</button>
                        <button type="button" role="menuitem" onClick={() => openSet(id)}>Edit</button>
                        <button
                          type="button"
                          role="menuitem"
                          class="danger-text"
                          onClick={() => ask('Delete set?', 'Delete', () => void deleteSet(id))}
                        >Delete</button>
                      </div>
                    </Show>
                  </div>
                </Show>
              </div>
            )}
          </For>
        </CardSection>
      </div>

      <SettingsTreeSheet
        title="Auto VJ"
        source={source}
        view={view()}
        open={sheetOpen()}
        onClose={() => setSheetOpen(false)}
      />

      {/* Over the sheet as well as the page, so a Remove asked from inside the sheet is answered on top. */}
      <Portal>
        <ConfirmModal
          open={Boolean(confirmation())}
          title={confirmation()?.title ?? ''}
          confirmLabel={confirmation()?.confirmLabel ?? ''}
          tone="danger"
          onConfirm={() => {
            const pending = confirmation();
            setConfirmation(undefined);
            pending?.run();
          }}
          onCancel={() => setConfirmation(undefined)}
        />
      </Portal>
    </>
  );
}
